import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Crown,
  Mail,
  Meh,
  Phone,
  Search,
  Smile,
  Sparkles,
  ThumbsUp,
} from "lucide-react";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { useGuestsPage, useUpdateGuest } from "@/hooks/use-guests";
import { isValidPhone, trimDialCodeOnly } from "@/lib/phone";
import type { Guest } from "@/store/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const PAGE_SIZE = 25;

const RATING_BADGES: Record<
  number,
  {
    label: string;
    icon: typeof Crown;
    cls: string;
    description: string;
  }
> = {
  5: {
    label: "Excelente",
    icon: Crown,
    cls: "bg-amber-100 text-amber-800 border-amber-200",
    description: "Cliente VIP — máxima prioridad",
  },
  4: {
    label: "Muy bueno",
    icon: ThumbsUp,
    cls: "bg-emerald-100 text-emerald-800 border-emerald-200",
    description: "Cliente frecuente y confiable",
  },
  3: {
    label: "Bueno",
    icon: Smile,
    cls: "bg-sky-100 text-sky-800 border-sky-200",
    description: "Cliente sin observaciones",
  },
  2: {
    label: "Regular",
    icon: Meh,
    cls: "bg-orange-100 text-orange-800 border-orange-200",
    description: "Cliente con incidentes ocasionales",
  },
  1: {
    label: "Riesgo",
    icon: AlertTriangle,
    cls: "bg-red-100 text-red-800 border-red-200",
    description: "Cliente con historial de no-shows o cancelaciones",
  },
};

function RatingBadge({ rating }: { rating: number }) {
  // Backend rating is a Decimal(3,2); bucket it into the 1–5 visual scale.
  const bucket = rating > 0 ? Math.min(5, Math.max(1, Math.round(rating))) : 0;
  const config = RATING_BADGES[bucket];
  if (!config) {
    return (
      <Badge variant="outline" className="gap-1 text-xs font-medium text-text-muted border-dashed">
        Sin calificar
      </Badge>
    );
  }
  const Icon = config.icon;
  return (
    <Badge className={`gap-1 text-xs font-medium ${config.cls}`} title={config.description}>
      <Icon className="size-3" />
      {config.label}
    </Badge>
  );
}

function Avatar({ name }: { name: string }) {
  const initials = useMemo(
    () =>
      name
        .split(" ")
        .map((w) => w[0] ?? "")
        .join("")
        .slice(0, 2)
        .toUpperCase() || "?",
    [name],
  );
  return (
    <div className="size-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold shrink-0">
      {initials}
    </div>
  );
}

function RatingLegend() {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-text-secondary">
      <span className="font-medium">Calificación:</span>
      {[5, 4, 3, 2, 1].map((r) => {
        const c = RATING_BADGES[r];
        const Icon = c.icon;
        return (
          <span
            key={r}
            className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 ${c.cls}`}
          >
            <Icon className="size-3" />
            {c.label}
          </span>
        );
      })}
    </div>
  );
}

export default function ClientePage() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [editingGuest, setEditingGuest] = useState<Guest | null>(null);
  // Filter state — both reset pagination to page 0 when changed.
  const [ratingFilter, setRatingFilter] = useState<"all" | "1" | "2" | "3" | "4" | "5" | "unrated">(
    "all",
  );
  const [vipFilter, setVipFilter] = useState<"all" | "vip" | "non-vip">("all");

  // Debounce search input — also reset to page 0 whenever the query changes
  // so we don't end up on a non-existent page of a smaller filtered set.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  // Reset to page 0 whenever a filter changes (independent of search debounce
  // so the snap is immediate when toggling filters).
  useEffect(() => {
    setPage(0);
  }, [ratingFilter, vipFilter]);

  const { data, isPending, isPlaceholderData } = useGuestsPage({
    search: debouncedSearch || undefined,
    ratingBucket:
      ratingFilter === "all" || ratingFilter === "unrated" ? undefined : Number(ratingFilter),
    isUnrated: ratingFilter === "unrated" ? true : undefined,
    isVip: vipFilter === "vip" ? true : vipFilter === "non-vip" ? false : undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const guests = data?.items ?? [];
  const total = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, (page + 1) * PAGE_SIZE);
  const isInitialLoad = isPending && !data;

  return (
    <div className="flex-1 overflow-y-auto px-6 py-10 sm:py-14 md:pl-24">
      <header className="mb-10 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-3">
        <div className="flex flex-wrap items-baseline gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Clientes</h1>
          <span className="text-sm text-text-muted tabular-nums">
            {total.toLocaleString("es-CO")} {total === 1 ? "registrado" : "registrados"}
          </span>
        </div>
        <RatingLegend />
      </header>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative w-full sm:flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-text-muted pointer-events-none" />
          <Input
            type="search"
            placeholder="Buscar por nombre, teléfono o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-11 pl-10 text-base"
          />
        </div>
        <select
          value={ratingFilter}
          onChange={(e) => setRatingFilter(e.target.value as typeof ratingFilter)}
          className="h-11 rounded-lg border border-input bg-background px-3 text-sm"
          aria-label="Filtrar por calificación"
        >
          <option value="all">Todas las calificaciones</option>
          <option value="5">Excelente</option>
          <option value="4">Muy bueno</option>
          <option value="3">Bueno</option>
          <option value="2">Regular</option>
          <option value="1">Riesgo</option>
          <option value="unrated">Sin calificar</option>
        </select>
        <select
          value={vipFilter}
          onChange={(e) => setVipFilter(e.target.value as typeof vipFilter)}
          className="h-11 rounded-lg border border-input bg-background px-3 text-sm"
          aria-label="Filtrar por VIP"
        >
          <option value="all">Todos</option>
          <option value="vip">Solo VIP</option>
          <option value="non-vip">Sin VIP</option>
        </select>
      </div>

      {isInitialLoad ? (
        <div className="flex items-center justify-center py-20">
          <Spinner className="size-8 text-text-secondary" />
        </div>
      ) : guests.length === 0 ? (
        <div className="bg-white border border-border rounded-xl px-6 py-16 text-center">
          <p className="text-text-muted">
            {debouncedSearch
              ? `No hay clientes que coincidan con "${debouncedSearch}".`
              : "Aún no hay clientes registrados."}
          </p>
        </div>
      ) : (
        <>
          <div
            className={`bg-white border border-border rounded-xl overflow-hidden transition-opacity ${
              isPlaceholderData ? "opacity-60" : "opacity-100"
            }`}
          >
            <Table>
              <TableHeader>
                <TableRow className="bg-surface-alt/60 border-b border-border">
                  <TableHead className="text-text-secondary font-medium">Cliente</TableHead>
                  <TableHead className="text-text-secondary font-medium">Teléfono</TableHead>
                  <TableHead className="text-text-secondary font-medium">Notas</TableHead>
                  <TableHead className="text-text-secondary font-medium w-44">
                    Calificación
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {guests.map((guest) => (
                  <TableRow
                    key={guest.id}
                    onClick={() => setEditingGuest(guest)}
                    className="cursor-pointer border-b border-border/60 hover:bg-surface-alt/40"
                  >
                    <TableCell className="py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar name={guest.name} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="font-semibold truncate">
                              {guest.name || "Sin nombre"}
                            </div>
                            {guest.isVip && (
                              <Badge className="shrink-0 gap-1 text-[10px] font-bold bg-amber-500 text-white border-transparent">
                                <Sparkles className="size-3" />
                                VIP
                              </Badge>
                            )}
                          </div>
                          {guest.email ? (
                            <div className="text-xs text-text-secondary flex items-center gap-1 truncate">
                              <Mail className="size-3 shrink-0" />
                              <span className="truncate">{guest.email}</span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-3">
                      {guest.phone ? (
                        <span className="inline-flex items-center gap-1.5 text-sm tabular-nums">
                          <Phone className="size-3 text-text-muted" />
                          {guest.phone}
                        </span>
                      ) : (
                        <span className="text-text-muted text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="py-3 max-w-xs">
                      {guest.notes ? (
                        <span
                          className="text-sm text-text-secondary line-clamp-1"
                          title={guest.notes}
                        >
                          {guest.notes}
                        </span>
                      ) : (
                        <span className="text-text-muted text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="py-3">
                      <RatingBadge rating={guest.rating} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-text-secondary tabular-nums">
              Mostrando {showingFrom}–{showingTo} de {total}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || isPlaceholderData}
                className="h-10 px-3"
              >
                <ChevronLeft className="size-4" /> Anterior
              </Button>
              <span className="text-sm tabular-nums px-3 text-text-secondary">
                {page + 1} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages - 1 || isPlaceholderData}
                className="h-10 px-3"
              >
                Siguiente <ChevronRight className="size-4" />
              </Button>
            </div>
          </div>
        </>
      )}

      <GuestDrawer
        guest={editingGuest}
        open={editingGuest !== null}
        onOpenChange={(open) => {
          if (!open) setEditingGuest(null);
        }}
      />
    </div>
  );
}

interface GuestDrawerProps {
  guest: Guest | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function GuestDrawer({ guest, open, onOpenChange }: GuestDrawerProps) {
  // Local form state seeded from the guest each time the drawer opens for a
  // different row. Without the keyed reset we'd carry edits from the
  // previous guest onto the new one.
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [rating, setRating] = useState(0);
  const [isVip, setIsVip] = useState(false);

  useEffect(() => {
    if (guest) {
      setName(guest.name);
      setPhone(guest.phone);
      setEmail(guest.email);
      setNotes(guest.notes);
      setRating(guest.rating);
      setIsVip(guest.isVip);
    }
  }, [guest?.id]);

  const updateMut = useUpdateGuest();

  if (!guest) {
    return null;
  }

  const isDirty =
    name !== guest.name ||
    phone !== guest.phone ||
    email !== guest.email ||
    notes !== guest.notes ||
    rating !== guest.rating ||
    isVip !== guest.isVip;
  const phoneInvalid = !isValidPhone(phone);
  const canSave = isDirty && name.trim().length > 0 && !phoneInvalid && !updateMut.isPending;

  const handleSave = () => {
    if (!canSave) return;
    const changes: Partial<Guest> = {};
    if (name.trim() !== guest.name) changes.name = name.trim();
    if (phone !== guest.phone) changes.phone = trimDialCodeOnly(phone);
    if (email !== guest.email) changes.email = email;
    if (notes !== guest.notes) changes.notes = notes;
    if (rating !== guest.rating) changes.rating = rating;
    if (isVip !== guest.isVip) changes.isVip = isVip;
    updateMut.mutate(
      { id: guest.id, changes },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  };

  const ratingButtons = [1, 2, 3, 4, 5] as const;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full data-[side=right]:sm:max-w-2xl data-[side=right]:pointer-coarse:sm:max-w-3xl flex flex-col"
      >
        <SheetHeader className="pb-4 border-b">
          <div className="flex items-center gap-3">
            <Avatar name={name || guest.name} />
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-lg font-semibold truncate">
                {name || guest.name || "Sin nombre"}
              </SheetTitle>
              <SheetDescription className="text-xs text-text-muted">
                Ficha del cliente — los cambios se guardan al confirmar.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <Label htmlFor="guest-name" className="text-xs uppercase tracking-wide text-text-muted">
              Nombre
            </Label>
            <Input
              id="guest-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-11 text-base"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="guest-phone"
                className="text-xs uppercase tracking-wide text-text-muted"
              >
                Teléfono
              </Label>
              {/* react-international-phone gives us a flag dropdown next to the
                  number and keeps ``value`` in E.164, which is what the
                  backend's PhoneNumberField stores. */}
              <PhoneInput
                inputProps={{ id: "guest-phone", autoComplete: "tel" }}
                defaultCountry="co"
                value={phone}
                onChange={(value: string) => setPhone(value)}
                className="phone-input"
                inputClassName="!h-11 !flex-1 !rounded-r-lg !rounded-l-none !border !border-l-0 !border-input !bg-transparent !px-3 !py-1 !text-base !outline-none focus-visible:!border-ring focus-visible:!ring-ring/50 focus-visible:!ring-[3px]"
                countrySelectorStyleProps={{
                  buttonClassName:
                    "!h-11 !rounded-l-lg !rounded-r-none !border !border-r-0 !border-input !bg-transparent !px-2",
                  dropdownStyleProps: {
                    className: "!bg-background !border-input !text-foreground",
                  },
                }}
              />
              {phoneInvalid && <p className="text-xs text-destructive">Teléfono inválido</p>}
            </div>
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="guest-email"
                className="text-xs uppercase tracking-wide text-text-muted"
              >
                Email
              </Label>
              <Input
                id="guest-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 text-base"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label
              htmlFor="guest-notes"
              className="text-xs uppercase tracking-wide text-text-muted"
            >
              Notas
            </Label>
            <Textarea
              id="guest-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Preferencias, alergias, mesa habitual…"
              className="text-base"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-xs uppercase tracking-wide text-text-muted">Calificación</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {ratingButtons.map((r) => {
                const c = RATING_BADGES[r];
                const Icon = c.icon;
                const active = Math.round(rating) === r;
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRating(r)}
                    className={`flex items-center justify-center gap-2 h-12 px-4 rounded-xl border text-sm transition ${
                      active
                        ? `${c.cls} border-current font-semibold`
                        : "border-input bg-background text-text-secondary hover:bg-muted/40"
                    }`}
                  >
                    <Icon className="size-4" />
                    {c.label}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setRating(0)}
                className={`flex items-center justify-center h-12 px-4 rounded-xl border text-sm transition ${
                  rating === 0
                    ? "border-foreground bg-muted/60 font-semibold"
                    : "border-dashed border-input bg-background text-text-secondary hover:bg-muted/40"
                }`}
              >
                Sin calificar
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-border bg-muted/30 px-4 py-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 font-medium">
                <Sparkles className="size-4 text-amber-500" />
                Marcar como VIP
              </div>
              <p className="text-xs text-text-secondary">
                Resalta al cliente en la lista y en el mapa de reservas.
              </p>
            </div>
            <Switch checked={isVip} onCheckedChange={setIsVip} />
          </div>
        </div>

        <SheetFooter className="flex-row border-t pt-4 gap-3">
          <Button
            size="xl"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
            disabled={updateMut.isPending}
          >
            Cancelar
          </Button>
          <Button size="xl" onClick={handleSave} disabled={!canSave} className="flex-1">
            {updateMut.isPending ? "Guardando…" : "Guardar"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
